import { htmlToText } from "html-to-text";
import { flattenDeep } from "lodash";
import moment from "moment";
import {
	calculateAgeFromBirthDate,
	getNodeCSSPropertyValue,
	getOrdinal,
	groupBy,
	isCountryCanada,
	isEmpty,
	isObject,
	isStateNewBrunswick,
	toCapitalCase,
} from "utils/helpers";
import vars from "utils/vars";
import wrap from "word-wrap";
import { getEffectiveProvinceForBcScreening, isBritishColumbiaProvince } from "./bcMinorAilmentScreening";
import { formatNotePatientFullName } from "./notePatientDisplay";
import { parsePatientDateOfBirthMoment } from "./patientDate";
import { firstLetterRegexp, outputCapitalizeLettersRegexp, outputDateCapitalizeFormatRegexp } from "./regexp";

export const MAX_LINE_WIDTH = 110;
export const SOAP_SECTION_NAMES = [
	vars.app.soapSections.subjective,
	vars.app.soapSections.basicSubjective,
	vars.app.soapSections.objective,
	vars.app.soapSections.basicObjective,
	vars.app.soapSections.assessments,
	vars.app.soapSections.plan,
];
const INITIAL_INDENTATION_CHARACTER = "  ";

export const wrapText = (text = "", options = {}) => wrap(text, { width: MAX_LINE_WIDTH, trim: true, ...options });

export const getIndentationString = ({
	indentation = 0,
	indentationCharacter = INITIAL_INDENTATION_CHARACTER,
	joinWith = "",
}) =>
	Array.apply(null, { length: indentation })
		.map(() => indentationCharacter)
		.join(joinWith);

export const splitSentence = (sentence, nChar) => {
	const chars = sentence.split("");
	let charCount = 0;
	let groupCount = 0;
	const characterGroups = chars.reduce(
		(groups, char) => {
			// if we don't have enough characters or we still have a word to process add the character to the group
			if (charCount < nChar || char !== " ") groups[groupCount].push(char);
			else {
				// we have reached the max. number of character and we also are at the end of word
				// => we need to create a new group
				groups.push([char]);
				// increase group count so we now add characters to a new group
				groupCount++;
				// reset character count
				charCount = 0;
			}
			charCount++;
			return groups;
		},
		[[]]
	);
	return characterGroups.map((group) => group.join(""));
};

export const isOrderChildrenHasAnswers = (order) => {
	if (order === null) return false;
	if (Array.isArray(order)) return order?.some((item) => item?.selected_answers?.length);
	if (!Array.isArray(order)) return Object.keys(order)?.some((item) => isOrderChildrenHasAnswers(order[item]));
	return false;
};

export const isQuestionHaveKeyword = ({ question = {}, keyword = "" }) =>
	(question?.keyword || "")?.toLowerCase()?.indexOf(keyword?.toLowerCase()) >= 0 || false;

export const sortQuestionsByOrder = (questions = []) =>
	[...(questions || [])]?.sort((a, b) => (a?.sort_order || 0) - (b?.sort_order || 0));

export const getQuestionsForSpecificKeyword = ({
	questions = [],
	keyword,
	type = null,
	parent_label = "",
	parent = {},
	parent_related_answer = {},
}) => {
	const isTypeNull = type === null;
	const isTypeString = typeof type === "string";
	const isTypeArray = Array.isArray(type);

	if (!keyword) throw new Error("Please Provide keyword to search for!");
	if (!isTypeNull && !isTypeString && !isTypeArray)
		throw new Error("Type parameter must be string or array or no passed at all!");

	return flattenDeep(
		questions
			.map((singleItem) =>
				isQuestionHaveKeyword({ question: singleItem, keyword }) &&
				((type &&
					((isTypeString && type?.toLowerCase() === singleItem?.type?.toLowerCase()) ||
						(isTypeArray &&
							type?.some((item) => item?.toLowerCase() === singleItem?.type?.toLowerCase())))) ||
					isTypeNull)
					? {
							...(singleItem || {}),
							...(parent?.custom_index ? { parent_custom_index: parent?.custom_index } : {}),
							...(parent_label ? { parent_label } : {}),
							parent_related_answer,
					  }
					: singleItem?.hasOwnProperty("dependency_questions") &&
					  Array.isArray(singleItem?.dependency_questions) &&
					  singleItem?.dependency_questions?.length
					? getQuestionsForSpecificKeyword({
							questions: [...(singleItem?.dependency_questions || [])],
							keyword,
							type,
							parent: singleItem,
							parent_label:
								isTypeGroup(singleItem?.type || "") &&
								(isTypeSelectText(parent?.type || "") ||
									isTypeSelectTextIcon(parent?.type || "") ||
									isTypeSelectTextImage(parent?.type || ""))
									? parent_label
									: singleItem?.label,
					  })
					: singleItem?.hasOwnProperty("children") &&
					  Array.isArray(singleItem?.children) &&
					  singleItem?.children?.length
					? getQuestionsForSpecificKeyword({
							questions: [...(singleItem?.children || [])],
							keyword,
							type,
							parent: singleItem,
							parent_label:
								isTypeGroup(singleItem?.type || "") &&
								(isTypeSelectText(parent?.type || "") ||
									isTypeSelectTextIcon(parent?.type || "") ||
									isTypeSelectTextImage(parent?.type || ""))
									? parent_label
									: singleItem?.label,
					  })
					: null
			)
			?.filter(Boolean)
	);
};

export const prepareDependenciesQuestionsWithRepeatedOnes = ({ questions = [], depth = 0 }) => {
	let updatedQuestions = [];
	questions.forEach(
		({
			set_as_not_examined: _set_as_not_examined,
			set_as_normal: _set_as_normal,
			selected_answers: _selected_answers,
			hide_dependency: _hide_dependency,
			output_text: _output_text,
			has_abnormal_answers: _has_abnormal_answers,
			normal_text: _normal_text,
			abnormal_text: _abnormal_text,
			dependency_questions: _dependency_questions,
			repeated_dependencies_limit,
			children = [],
			note_questions = [],
			...singleQuestion
		}) => {
			const noteQuestionsLength = note_questions?.length;
			const sharableQuestionsAttributes = {
				...(singleQuestion || {}),
				note_questions,
				repeated_dependencies_limit: Math.max(+repeated_dependencies_limit - +noteQuestionsLength, 0),
			};
			updatedQuestions = [
				...(updatedQuestions || []),
				...(!noteQuestionsLength
					? [
							{
								...(sharableQuestionsAttributes || {}),
								children: [
									...(prepareDependenciesQuestionsWithRepeatedOnes({
										questions: [...(children || [])],
										depth: depth + 1,
									}) || []),
								],
							},
					  ]
					: note_questions.map((singleNoteQuestion) => ({
							...(sharableQuestionsAttributes || {}),
							...(singleNoteQuestion || {}),
							sort_order:
								sharableQuestionsAttributes.sort_order ?? singleNoteQuestion.sort_order,
							dependency_questions: [
								...(prepareDependenciesQuestionsWithRepeatedOnes({
									questions: [...(singleNoteQuestion?.dependency_questions || [])],
									depth: depth + 1,
								}) || []),
							],
							children: [
								...(prepareDependenciesQuestionsWithRepeatedOnes({
									questions: [
										...(Array.isArray(singleNoteQuestion?.children) &&
										!singleNoteQuestion?.children?.length
											? [...(children || [])]
											: [...(singleNoteQuestion?.children || [])]),
									],
									depth: depth + 1,
								}) || []),
							],
					  })) || []),
			];
		}
	);
	return updatedQuestions;
};

export const prepareNoteWithRepeatedQuestions = (note = {}, { procedures, orders, questions, ...newNote }) => ({
	...(note || {}),
	...(newNote || {}),
	...(procedures
		? {
				procedures: [
					...([...(procedures || [])]?.map(({ question, ...procedure }) => ({
						...procedure,
						question:
							prepareDependenciesQuestionsWithRepeatedOnes({
								questions: [question],
							})?.[0] || {},
					})) || []),
				],
		  }
		: {}),
	...(orders ? { orders: { ...(prepareOrdersWithRepeatedQuestions({ orders }) || {}) } } : {}),
	...(questions
		? {
				questions: {
					...(questions || {}),
					...(questions?.subjective
						? {
								subjective: [
									...(prepareDependenciesQuestionsWithRepeatedOnes({
										questions: [
											...(sortQuestionsByAPIOrder({
												questions: [...(questions?.subjective || [])],
											}) || []),
										],
									}) || []),
								],
						  }
						: {}),
					...(questions?.objective
						? {
								objective: [
									...(prepareDependenciesQuestionsWithRepeatedOnes({
										questions: [...(questions?.objective || [])],
									}) || []),
								],
						  }
						: {}),
				},
		  }
		: {}),
});

export const prepareOrdersWithRepeatedQuestions = ({ orders = {}, depth = 0 }) => {
	const preparedOrders = [
		...(Object.keys(orders)?.map((key) => {
			const singleOrder = orders?.[key];
			return {
				[key]:
					singleOrder !== null && !Array.isArray(singleOrder)
						? prepareOrdersWithRepeatedQuestions({ orders: singleOrder, depth: depth + 1 })
						: [
								...(prepareDependenciesQuestionsWithRepeatedOnes({
									questions: [...(singleOrder || [])],
								}) || []),
						  ],
			};
		}) || []),
	].reduce((a, v) => ({ ...(a || {}), ...(v || {}) }), {});
	return preparedOrders;
};

export const getAllowedNestedQuestions = ({
	questions = [],
	allowDepQuestions = true,
	allowGroupRepeatedQuestion = false,
	section = "",
	isPatientHistory = false,
	isSinglePatientHistory = false,
}) => [
	...(questions?.filter(
		({
			dependency_questions = [],
			children = [],
			log = null,
			set_as_not_examined = null,
			output_text,
			normal_text,
			abnormal_text,
			selected_answers,
			type,
		}) =>
			[
				...(isSinglePatientHistory
					? [
							...(selected_answers &&
							((Array.isArray(selected_answers) && selected_answers?.length) ||
								(isObject(selected_answers) && Object.keys(selected_answers).length))
								? [selected_answers]
								: []),
					  ]
					: isPatientHistory
					? [
							...(log &&
							((isObject(log) && Object.keys(log).length) || (Array.isArray(log) && log.length))
								? [log]
								: []),
					  ]
					: [
							...(section === vars.app.soapSections.plan && selected_answers ? [selected_answers] : []),
							...(section !== vars.app.soapSections.plan && (output_text || normal_text || abnormal_text)
								? [output_text || normal_text || abnormal_text]
								: []),
							...(section !== vars.app.soapSections.plan &&
							selected_answers &&
							((Array.isArray(selected_answers) && selected_answers.length) ||
								(typeof selected_answers === "string" && selected_answers.trim()))
								? [selected_answers]
								: []),
							...(section !== vars.app.soapSections.plan &&
							log &&
							((isObject(log) && Object.keys(log).length) || (Array.isArray(log) && log.length))
								? [log]
								: []),
							...(allowGroupRepeatedQuestion &&
							section !== vars.app.soapSections.plan &&
							!selected_answers &&
							!output_text &&
							!normal_text &&
							!abnormal_text
								? [type]
								: []),
					  ]),
				...(allowDepQuestions
					? getAllowedNestedQuestions({
							questions: dependency_questions,
							allowDepQuestions,
							section,
							isPatientHistory,
							isSinglePatientHistory,
					  }) || []
					: []),
				...(getAllowedNestedQuestions({
					questions: children,
					allowDepQuestions,
					section,
					isPatientHistory,
					isSinglePatientHistory,
				}) || []),
			].filter(Boolean).length ||
			(!isPatientHistory && !isSinglePatientHistory && set_as_not_examined)
	) || []),
];

export const isQuestionKeywordDrugUnit = ({ keyword = "" }) =>
	isQuestionHaveKeyword({ question: { keyword }, keyword: vars.app.questionKeywords.drugUnit }) || false;

export const isQuestionKeywordColoredAnswers = ({ keyword = "" }) =>
	isQuestionHaveKeyword({ question: { keyword }, keyword: vars.app.questionKeywords.coloredAnswers }) || false;

export const isQuestionKeywordChart = ({ keyword = "" }) =>
	isQuestionHaveKeyword({ question: { keyword }, keyword: vars.app.questionKeywords.chart }) || false;

export const isQuestionKeywordLineChart = ({ keyword = "" }) =>
	isQuestionHaveKeyword({ question: { keyword }, keyword: vars.app.questionKeywords.lineChart }) || false;

export const isQuestionKeywordTemperatureChart = ({ keyword = "" }) =>
	isQuestionHaveKeyword({ question: { keyword }, keyword: vars.app.questionKeywords.temperatureChart }) || false;

export const isQuestionKeywordBloodPressure = ({ keyword = "" }) =>
	isQuestionHaveKeyword({ question: { keyword }, keyword: vars.app.questionKeywords.bloodPressure }) || false;

export const isQuestionHasLabelSubString = ({ question = {}, substring = "" }) =>
	(question?.label || "")?.toLowerCase()?.indexOf(substring) >= 0;

export const isQuestionIsVitalSignQuestion = ({ question = {} }) =>
	isQuestionHaveKeyword({ question, keyword: vars.app.questionKeywords.vitalSigns }) || false;

export const VITAL_SIGN_RECORD_METRICS = {
	WEIGHT: "weight",
	HEART_RATE: "heart_rate",
	BLOOD_SUGAR: "blood_sugar",
	BLOOD_PRESSURE: "blood_pressure",
};

const VITAL_SIGN_METRIC_KEYWORD_SETS = {
	[VITAL_SIGN_RECORD_METRICS.WEIGHT]: [["vital-sign", "weight"]],
	[VITAL_SIGN_RECORD_METRICS.HEART_RATE]: [["vital-sign", "heart-rate"]],
	[VITAL_SIGN_RECORD_METRICS.BLOOD_SUGAR]: [
		["vital-sign", "blood-sugar"],
		["blood-sugar", "line-chart"],
	],
	[VITAL_SIGN_RECORD_METRICS.BLOOD_PRESSURE]: [
		["vital-sign", "blood-pressure"],
		["blood-pressure", "chart"],
	],
};

const questionMatchesKeywordSet = (question = {}, keywordSet = []) =>
	keywordSet.every((keyword) => isQuestionHaveKeyword({ question, keyword }));

export const isQuestionMatchingVitalMetric = (question = {}, metric = "") => {
	const keywordSets = VITAL_SIGN_METRIC_KEYWORD_SETS[metric];
	if (!keywordSets?.length) return false;

	return keywordSets.some((keywordSet) => questionMatchesKeywordSet(question, keywordSet));
};

export const flattenBasicQuestions = (questions = []) =>
	flattenDeep([
		...(questions?.map(({ children, ...parentQuestion }) =>
			[...(children || [])]?.map((childQuestion) => ({
				...(childQuestion || {}),
				parent: parentQuestion,
			}))
		) || []),
	]);

/** Some tenants omit "blood-sugar" from question keywords (e.g. only vital-signs + line-chart). */
export const questionLooksLikeBloodSugar = (question = {}) => {
	const label = (question?.label || "").toLowerCase();
	const keyword = (question?.keyword || "").toLowerCase();
	const normalizedLabel = label.replace(/\s/g, "");
	const normalizedKeyword = keyword.replace(/[-\s]/g, "");

	return (
		label.includes("blood sugar") ||
		normalizedLabel.includes("bloodsugar") ||
		keyword.includes("blood-sugar") ||
		normalizedKeyword.includes("bloodsugar")
	);
};

export const findVitalSignQuestionByMetric = (questions = [], metric = "") => {
	if (!metric) return null;

	const flattenedQuestions = flattenBasicQuestions(questions);
	const keywordMatch = flattenedQuestions.find((question) => isQuestionMatchingVitalMetric(question, metric));

	if (keywordMatch) return keywordMatch;

	if (metric === VITAL_SIGN_RECORD_METRICS.BLOOD_SUGAR) {
		return flattenedQuestions.find((question) => questionLooksLikeBloodSugar(question)) || null;
	}

	return null;
};

export const getPatientHistoryQuestionId = (question = {}) =>
	[question?.note_question_id || "", question?.id || "", question?.key || 1, question?.sort_order || 0]
		.filter(Boolean)
		.join("-");

export const buildMedicalHistoryAddRecordPath = (metric = "") =>
	`/user/medical-history?add_record=${encodeURIComponent(metric)}`;

export const isQuestionSectionIsAPlanSection = ({ section = "" }) =>
	section?.toLowerCase() === vars.app.soapSections.plan || false;

export const checkQuestionType = ({ question = {}, type = "" }) => {
	if (isEmpty(question) || !type) throw new Error("question or type params mustn't be empty!");
	return question?.type?.startsWith(type) || false;
};

export const getQuestionOutputText = ({ question, isPlanSection = false, section = "" }) => {
	if (isTypePatientSignature(question?.type || "")) {
		return "";
	}

	const hasOutputText = question?.output_text || question?.abnormal_text || question?.normal_text;
	if (hasOutputText) return hasOutputText;

	if (isPlanSection && !Array.isArray(question?.selected_answers) && question?.selected_answers) {
		return (
			question.selected_answers
				?.split("$$$")
				?.sort((a, b) => a.localeCompare(b))
				?.map((value, i) =>
					question.selected_answers?.split("$$$").length > 1 &&
					i === question.selected_answers?.split("$$$")?.sort((a, b) => a.localeCompare(b)).length - 1
						? `and ${value}`
						: value
				)
				?.join(", ")
				?.split(", and")
				.join(" and") || ""
		);
	}

	const isSubjectiveOrObjective = [vars.app.soapSections.subjective, vars.app.soapSections.objective].includes(
		section?.toLowerCase()
	);
	if (
		isSubjectiveOrObjective &&
		question?.selected_answers &&
		((Array.isArray(question.selected_answers) && question.selected_answers.length) ||
			(typeof question.selected_answers === "string" && question.selected_answers.trim()))
	) {
		if (typeof question.selected_answers === "string") {
			const trimmed = question.selected_answers.trim();
			if (trimmed.includes("$$$")) {
				const parts = trimmed.split("$$$").sort((a, b) => a.localeCompare(b));
				return (
					parts
						?.map((value, i) => (parts.length > 1 && i === parts.length - 1 ? `and ${value}` : value))
						?.join(", ")
						?.split(", and")
						.join(" and") || ""
				);
			}
			return trimmed;
		}
		return (
			question?.answers
				?.filter(({ id }) => question.selected_answers?.includes(id))
				?.map(({ value }) => value)
				?.join(" - ") ||
			question.selected_answers?.join(" - ") ||
			""
		);
	}

	return "";
};

export const prepareNoteSingleQuestionSection = ({
	question = {},
	nextQuestion = {},
	prevVitalQuestion = null,
	indentation = 0,
	index = 0,
	depth = 0,
	user = {},
	withTrackingData = false,
	t,
	allowLabel = true,
	allowAnswerBesideLabel = false,
	section = "",
	isCopy = false,
	isPharmacySegment = false,
	prevGroupRepeatedQuestionWithoutAnyOutput = false,
	isPatientHistory = false,
	treeView = false,
	allowHTML = false,
}) => {
	const isSectionIsSOAPSection = (section && !SOAP_SECTION_NAMES?.includes(section?.toLowerCase())) || false;

	// checking if section name matches SOAP sections names.
	if (!isPatientHistory && isSectionIsSOAPSection) throw new Error("section parameter must be one of SOAP sections.");
	// check if question object is not empty.
	if (isEmpty(question)) return;
	// check if previous vital question is object and is not empty.
	if (isObject(prevVitalQuestion) && isEmpty(question)) return;
	// check if question has keyword drug-unit
	if (!isPatientHistory && isPharmacySegment && isQuestionKeywordDrugUnit({ keyword: question?.keyword || "" }))
		return;

	// question object
	const isPlanSection = isQuestionSectionIsAPlanSection({ section });
	const isFreeTextQuestion = checkQuestionType({ question, type: vars.app.questionTypes.freeText });
	const isNextQuestionKeywordDrugUnit = isQuestionKeywordDrugUnit({ keyword: nextQuestion?.keyword || "" });
	const questionIndentationDepth = depth || 1;
	const questionIndentation = questionIndentationDepth - 1 + (indentation - questionIndentationDepth + 1 || 1);
	const questionAnswersAndLogsIndentation = questionIndentation + 1;
	// Preserve HTML color styling in Tree View, otherwise convert to plain text
	const rawQuestionOutput = `${getQuestionOutputText({ question, isPlanSection, section })}${
		isNextQuestionKeywordDrugUnit && isPharmacySegment ? ` ${nextQuestion?.selected_answers || ""}` : ""
	}`;
	const questionOutputText =
		treeView && allowHTML
			? rawQuestionOutput // Preserve HTML (including color from abnormal_text)
			: htmlToText(rawQuestionOutput, {
					wordwrap: false,
					...(isFreeTextQuestion ? { whitespaceCharacters: " \t\r\f\u200b" } : {}),
			  });
	// Preserve HTML color styling in Tree View for vital questions
	const prevVitalQuestionRawOutput =
		prevVitalQuestion?.output_text || prevVitalQuestion?.normal_text || prevVitalQuestion?.abnormal_text;
	const prevVitalQuestionOutputText =
		treeView && allowHTML
			? prevVitalQuestionRawOutput // Preserve HTML
			: prevVitalQuestionRawOutput
			? htmlToText(prevVitalQuestionRawOutput, { wordwrap: false })
			: "";
	const questionLabel = question?.label || "";
	const questionNestedQuestions = [
		...(getAllowedNestedQuestions({
			questions: [...(question?.dependency_questions || []), ...(question?.children || [])],
			section,
		}) || []),
	];
	const vitalQuestionLabel = prevVitalQuestion?.label || questionLabel;
	const isQuestionNotExamined = question?.set_as_not_examined || false;
	const isFirstQuestionDepth = depth === 0;
	const isParagraphView = !allowLabel && !withTrackingData && !allowAnswerBesideLabel;
	const isQuestionHasOutput = Boolean(questionOutputText || "") || false;
	const isPrevVitalQuestionHasOutput = Boolean(prevVitalQuestionOutputText || "") || false;
	const isQuestionVitalSigns = (isQuestionIsVitalSignQuestion({ question }) && !isCopy) || false;
	const isQuestionSystolic = isQuestionHasLabelSubString({
		question: question,
		substring: vars.app.questionKeywords.systolic,
	});
	const isQuestionDiastolic = isQuestionHasLabelSubString({
		question: question,
		substring: vars.app.questionKeywords.diastolic,
	});
	const isPrevVitalQuestionSystolic = isQuestionHasLabelSubString({
		question: prevVitalQuestion,
		substring: vars.app.questionKeywords.systolic,
	});
	const isPrevVitalQuestionDiastolic = isQuestionHasLabelSubString({
		question: prevVitalQuestion,
		substring: vars.app.questionKeywords.diastolic,
	});
	const isQuestionIsVitalAndHasSubStringLabel = isQuestionVitalSigns && (isQuestionSystolic || isQuestionDiastolic);
	const isIndentationAllowedBeforeIndexQuestion =
		(isQuestionIsVitalAndHasSubStringLabel && isObject(prevVitalQuestion) && !isFirstQuestionDepth) ||
		(!isQuestionIsVitalAndHasSubStringLabel &&
			(allowLabel || withTrackingData || isQuestionNotExamined) &&
			!isFirstQuestionDepth);
	const isQuestionHasNestedQuestions = Boolean(questionNestedQuestions?.length || 0);
	const nestedQuestionsAllowedForRepeatedQuestions = getAllowedNestedQuestions({
		questions: [...(question?.note_questions || [])]?.map(
			({ type: _type, repeated_dependencies: _repeated_dependencies, ...item }) => ({
				...(item || {}),
				type: question?.type,
				repeated_dependencies: question?.repeated_dependencies,
			})
		),
		section,
		allowGroupRepeatedQuestion: true,
	});
	const nestedQuestionsAllowedForRepeatedQuestionsLength = nestedQuestionsAllowedForRepeatedQuestions?.length;
	const isQuestionLogAllowed =
		!isQuestionIsVitalAndHasSubStringLabel &&
		withTrackingData &&
		questionOutputText &&
		question?.log &&
		!isEmpty(question?.log);

	// prepare final question text.
	const questionIndentationString = isIndentationAllowedBeforeIndexQuestion
		? `\n${getIndentationString({ indentation: questionIndentation })}`
		: "";
	const questionNestedIndentationString = `\n${getIndentationString({
		indentation: questionAnswersAndLogsIndentation,
	})}`;

	// Format vital question label for tree view - only format if depth === 0 (main question)
	const vitalQuestionLabelText = vitalQuestionLabel?.split(" - ")?.[0] || "";
	const shouldFormatVitalLabel =
		treeView &&
		allowHTML &&
		depth === 0 &&
		vitalQuestionLabelText &&
		typeof vitalQuestionLabelText === "string" &&
		vitalQuestionLabelText.trim().length > 0;
	const formattedVitalQuestionLabel = shouldFormatVitalLabel
		? `<strong><u>${vitalQuestionLabelText}</u></strong>`
		: vitalQuestionLabelText;
	// In Tree View: only sub-questions (depth > 0) get bullet points for vital questions
	const vitalQuestionBullet =
		treeView && allowHTML
			? depth === 0
				? ""
				: "• " // Main questions: no bullet, sub-questions: bullet (•)
			: "- ";
	const finalVitalQuestionLabel = `${vitalQuestionBullet}${formattedVitalQuestionLabel}`;

	const finalVitalQuestionOutput =
		isQuestionHasOutput && isPrevVitalQuestionHasOutput
			? `: ${
					isQuestionSystolic
						? `${questionOutputText}`
						: isPrevVitalQuestionSystolic
						? `${prevVitalQuestionOutputText}`
						: ""
			  }/${
					isQuestionDiastolic
						? `${questionOutputText}`
						: isPrevVitalQuestionDiastolic
						? `${prevVitalQuestionOutputText}`
						: ""
			  } ${vitalQuestionLabel?.split(" - ")?.slice(-1)?.[0]?.split(" ")?.slice(-1)?.[0] || ""}`
			: "";
	const finalQuestionLog = isQuestionLogAllowed
		? `${questionNestedIndentationString}${t("titles.by")}: ${question?.log?.user?.name || ""} - ${
				question?.log?.created_at?.time || ""
		  }`
		: "";
	// In Tree View: Recursively extract all output text from nested questions (for comma-separated paragraph)
	const extractAllNestedOutputs = (nestedQuestions) => {
		if (!nestedQuestions?.length) return [];

		const outputs = [];
		nestedQuestions.forEach((nestedQuestion) => {
			// Get output from this question
			const nestedOutput = getQuestionOutputText({
				question: nestedQuestion,
				isPlanSection,
				section,
			});
			if (nestedOutput) {
				outputs.push(treeView && allowHTML ? nestedOutput : htmlToText(nestedOutput, { wordwrap: false }));
			}

			// Recursively get outputs from children and dependency_questions
			const childQuestions = [
				...(getAllowedNestedQuestions({
					questions: [...(nestedQuestion?.dependency_questions || []), ...(nestedQuestion?.children || [])],
					section,
				}) || []),
			];
			if (childQuestions?.length > 0) {
				outputs.push(...extractAllNestedOutputs(childQuestions));
			}
		});

		return outputs;
	};

	// In Tree View: Extract output from a single question and all its nested children (for one bullet point)
	const extractQuestionWithNestedOutputs = (targetQuestion) => {
		const outputs = [];

		// Get this question's output
		const questionOutput = getQuestionOutputText({
			question: targetQuestion,
			isPlanSection,
			section,
		});
		if (questionOutput) {
			outputs.push(treeView && allowHTML ? questionOutput : htmlToText(questionOutput, { wordwrap: false }));
		}

		// Get all outputs from its children/dependencies recursively
		const childQuestions = [
			...(getAllowedNestedQuestions({
				questions: [...(targetQuestion?.dependency_questions || []), ...(targetQuestion?.children || [])],
				section,
			}) || []),
		];
		if (childQuestions?.length > 0) {
			outputs.push(...extractAllNestedOutputs(childQuestions));
		}

		return outputs.filter(Boolean);
	};

	// In Tree View: no prefix for outputs at depth 0 (we'll add bullet when combining all outputs)
	// Sub-questions (depth > 0) won't be rendered separately in Tree View
	const outputPrefix =
		treeView && allowHTML
			? "" // No prefix in Tree View, we'll handle bullet when combining
			: allowLabel || withTrackingData
			? questionOutputText && !allowAnswerBesideLabel
				? questionNestedIndentationString
				: ""
			: nestedQuestionsAllowedForRepeatedQuestionsLength >= 2
			? ": "
			: !prevGroupRepeatedQuestionWithoutAnyOutput
			? `, `
			: "";

	const finalQuestionOutput =
		!isQuestionIsVitalAndHasSubStringLabel && questionOutputText ? `${outputPrefix}${questionOutputText}` : "";
	// In Tree View: only sub-questions (depth > 0) get bullet points, main questions (depth === 0) don't
	const finalQuestionLabelStart =
		treeView && allowHTML
			? depth === 0
				? ""
				: "• " // Main questions: no bullet, sub-questions: bullet (•)
			: isParagraphView && nestedQuestionsAllowedForRepeatedQuestionsLength >= 2
			? `,${depth ? " " : ""}for `
			: "- ";
	const getLabelText = () => {
		if (isQuestionVitalSigns && isQuestionHasLabelSubString({ question, substring: "per" }) && !isPlanSection) {
			return (question?.label?.split("(")?.[0] || "")?.trim() || "";
		} else if (!allowLabel && !withTrackingData && !isNaN(+question?.label?.trim().slice(-1))) {
			return `${getOrdinal(index + 1)} ${question?.label?.trim()?.slice(0, -1)}`;
		} else if ((question?.custom_index || "")?.split(" - ")?.slice(0, 1)?.join(" ")) {
			return `${(question?.custom_index || "")?.split(" - ")?.slice(0, 1)?.join(" ")?.trim()} `;
		} else {
			return question?.label?.trim();
		}
	};

	const labelText = getLabelText();
	// In Tree View: only main questions (depth === 0) get bold and underline, sub-questions don't
	const shouldFormatLabel =
		treeView &&
		allowHTML &&
		depth === 0 &&
		labelText &&
		typeof labelText === "string" &&
		labelText.trim().length > 0;
	const formattedLabel = shouldFormatLabel ? `<strong><u>${labelText}</u></strong>` : labelText;

	// In Tree View: hide labels for sub-questions (depth > 0), show only for main questions
	const shouldShowLabel =
		treeView && allowHTML
			? depth === 0 &&
			  (allowLabel ||
					(!allowLabel && isQuestionNotExamined) ||
					withTrackingData ||
					nestedQuestionsAllowedForRepeatedQuestionsLength >= 2 ||
					(isPlanSection && question?.selected_answers))
			: allowLabel ||
			  (!allowLabel && isQuestionNotExamined) ||
			  withTrackingData ||
			  nestedQuestionsAllowedForRepeatedQuestionsLength >= 2 ||
			  (isPlanSection && question?.selected_answers);

	const finalQuestionLabel =
		!isQuestionIsVitalAndHasSubStringLabel && shouldShowLabel ? `${finalQuestionLabelStart}${formattedLabel}` : "";
	const finalQuestionNestedQuestions = isQuestionHasNestedQuestions
		? (() => {
				// In Tree View at depth 0: Skip nested questions here, they'll be combined with main output
				if (treeView && allowHTML && depth === 0) {
					return "";
				}

				// Default behavior: render nested questions normally
				return questionNestedQuestions
					?.map((singleNestedQuestion, singleNestedQuestionIndex, singleNestedQuestionRowList) => {
						const isNotFirstNestedQuestionIndex = singleNestedQuestionIndex > 0;
						const isPrevGroupRepeatedQuestionWithoutAnyOutput =
							question?.note_questions?.length >= 2 &&
							!question?.output_text &&
							!question?.abnormal_text &&
							!question?.normal_text &&
							!(isQuestionSectionIsAPlanSection({ section }) && question?.selected_answers);
						const prevSingleNestedQuestion = {
							...(singleNestedQuestionRowList?.[singleNestedQuestionIndex - 1] || {}),
						};
						const nextSingleNestedQuestion = {
							...(singleNestedQuestionRowList?.[singleNestedQuestionIndex + 1] || {}),
						};

						return prepareNoteSingleQuestionSection({
							question: singleNestedQuestion,
							nextQuestion: nextSingleNestedQuestion,
							...(isNotFirstNestedQuestionIndex &&
							isQuestionIsVitalSignQuestion({ question: prevSingleNestedQuestion }) &&
							(isQuestionHasLabelSubString({
								question: prevSingleNestedQuestion,
								substring: vars.app.questionKeywords.systolic,
							}) ||
								isQuestionHasLabelSubString({
									question: prevSingleNestedQuestion,
									substring: vars.app.questionKeywords.diastolic,
								}))
								? { prevVitalQuestion: prevSingleNestedQuestion }
								: {}),
							prevGroupRepeatedQuestionWithoutAnyOutput: isPrevGroupRepeatedQuestionWithoutAnyOutput,
							indentation: indentation + 1,
							user,
							depth: depth + 1,
							withTrackingData,
							index: singleNestedQuestionIndex,
							treeView,
							allowHTML,
							t,
							allowLabel,
							allowAnswerBesideLabel,
							section,
							isPharmacySegment,
						});
					})
					.filter(Boolean)
					.map((item, itemIndex) => (!itemIndex ? item : item.replace(/(^, )/gi, "")))
					.map((item) =>
						isParagraphView
							? item
									.replace(outputCapitalizeLettersRegexp, (match) => match.toLowerCase())
									.replace(outputDateCapitalizeFormatRegexp, (match) =>
										match.replace(firstLetterRegexp, (x) => x.toUpperCase())
									)
							: item
					)
					.join(isParagraphView ? ", " : "");
		  })()
		: "";

	const finalVitalQuestion = `${finalVitalQuestionLabel}${finalVitalQuestionOutput}`;

	// In Tree View at depth 0: Create bullet points based on question type/keyword
	// - Simple questions get combined into current bullet
	// - GROUP questions or questions with "grouped-answer" keyword start a new bullet
	const combinedOutputForTreeView =
		treeView && allowHTML && depth === 0
			? (() => {
					const bulletPoints = [];
					let currentBulletOutputs = [];

					// Start with main question output
					if (questionOutputText) {
						currentBulletOutputs.push(questionOutputText.trim());
					}

					// Process each first-level dependency question
					if (isQuestionHasNestedQuestions) {
						questionNestedQuestions.forEach((nestedQuestion) => {
							const isGroupType = isTypeGroup(nestedQuestion?.type || "");
							const hasGroupedAnswerKeyword = isQuestionHaveKeyword({
								question: nestedQuestion,
								keyword: "grouped-answer",
							});

							if (isGroupType || hasGroupedAnswerKeyword) {
								// Finish current bullet if we have accumulated outputs
								if (currentBulletOutputs.length > 0) {
									bulletPoints.push(`• ${currentBulletOutputs.join(", ")}`);
									currentBulletOutputs = [];
								}

								// Create new bullet for this question with all its nested outputs
								const nestedOutputs = extractQuestionWithNestedOutputs(nestedQuestion);
								if (nestedOutputs.length > 0) {
									bulletPoints.push(`• ${nestedOutputs.join(", ")}`);
								}
							} else {
								// Add to current bullet (simple question without special handling)
								const nestedOutput = getQuestionOutputText({
									question: nestedQuestion,
									isPlanSection,
									section,
								});
								if (nestedOutput) {
									currentBulletOutputs.push(
										treeView && allowHTML
											? nestedOutput
											: htmlToText(nestedOutput, { wordwrap: false })
									);
								} else {
									// If no direct output, check for dependency_questions with content
									// This handles questions like "Abnormal lung sounds" that have nested answers
									const nestedOutputs = extractQuestionWithNestedOutputs(nestedQuestion);
									if (nestedOutputs.length > 0) {
										// Finish current bullet if we have accumulated outputs
										if (currentBulletOutputs.length > 0) {
											bulletPoints.push(`• ${currentBulletOutputs.join(", ")}`);
											currentBulletOutputs = [];
										}
										bulletPoints.push(`• ${nestedOutputs.join(", ")}`);
									}
								}
							}
						});

						// Output any remaining accumulated outputs as final bullet
						if (currentBulletOutputs.length > 0) {
							bulletPoints.push(`• ${currentBulletOutputs.join(", ")}`);
						}
					} else if (currentBulletOutputs.length > 0) {
						// No nested questions, just output the main
						bulletPoints.push(`• ${currentBulletOutputs.join(", ")}`);
					}

					// Join bullet points with line breaks and indentation
					if (bulletPoints.length > 0) {
						return bulletPoints.join("<br/>&nbsp;&nbsp;");
					}
					return "";
			  })()
			: null;

	const finalQuestion = `${finalQuestionLabel}${
		allowAnswerBesideLabel && finalQuestionOutput
			? `: ${finalQuestionOutput} ${
					isQuestionVitalSigns && isQuestionHasLabelSubString({ question, substring: "per" })
						? (question?.label || "")?.match(/\((.*?)\)/)?.[0] || ""
						: ""
			  }`
			: ""
	}${
		isQuestionNotExamined
			? ` (${t("actions.not_examined")})`
			: `${
					!allowAnswerBesideLabel
						? combinedOutputForTreeView !== null
							? `<br/>&nbsp;&nbsp;${combinedOutputForTreeView}` // Add newline + indentation before combined outputs in Tree View
							: finalQuestionOutput
						: ""
			  }${finalQuestionLog}${combinedOutputForTreeView !== null ? "" : finalQuestionNestedQuestions}`
	}`;

	// In Tree View: skip rendering sub-questions (depth > 0) since their outputs are collected by parent
	if (treeView && allowHTML && depth > 0) {
		return "";
	}

	// return final question string.
	return `${questionIndentationString}${
		isQuestionIsVitalAndHasSubStringLabel && isObject(prevVitalQuestion) ? finalVitalQuestion : finalQuestion
	}`;
};

export const prepareNoteQuestionsSection = ({
	questions = [],
	indentation = 0,
	user = {},
	withTrackingData = false,
	t,
	allowLabel = true,
	allowAnswerBesideLabel = false,
	section = "",
	isPharmacySegment = false,
	isPatientHistory = false,
	treeView = false,
	allowHTML = false,
}) => {
	// checking if section name matches SOAP sections names.
	if (section && !SOAP_SECTION_NAMES?.includes(section?.toLowerCase()))
		throw new Error("section parameter must be one of SOAP sections.");
	// check if questions array is not empty.
	if (isEmpty(questions)) return;
	// check if view is paragraph view.
	const isParagraphView = !allowLabel && !withTrackingData && !allowAnswerBesideLabel;
	// get allowed nested questions to loop through.
	const allowedQuestionsArray = getAllowedNestedQuestions({ questions, section, isPatientHistory });
	// get questions ready as a single string for each question as a separate line.
	const preparedAllowedQuestionsArray = allowedQuestionsArray
		?.map((question, index, row) =>
			prepareNoteSingleQuestionSection({
				question,
				nextQuestion: { ...(row?.[index + 1] || {}) },
				indentation,
				user,
				index,
				withTrackingData,
				t,
				allowLabel,
				allowAnswerBesideLabel,
				section,
				isPharmacySegment,
				isPatientHistory,
				treeView,
				allowHTML,
			})
		)
		.filter(Boolean)
		.map((item) => item.replace(/(^, )/gi, ""))
		.map((item) =>
			isParagraphView
				? item
						.replace(outputCapitalizeLettersRegexp, (match) => match.toLowerCase())
						.replace(outputDateCapitalizeFormatRegexp, (match) =>
							match.replace(firstLetterRegexp, (x) => x.toUpperCase())
						)
				: item
		)
		.filter((item) => Boolean(item?.trim()))
		.map((item, index, row) => {
			// Don't apply toCapitalCase to HTML content in tree view
			const processedItem = treeView && allowHTML && item.includes("<strong><u>") ? item : toCapitalCase(item);
			return `${processedItem}${
				row?.length - 1 !== index
					? allowLabel || withTrackingData || isParagraphView
						? `${isParagraphView ? "." : ""}${
								!allowLabel && !withTrackingData ? "\n" : ""
						  }\n${getIndentationString({ indentation })}`
						: "- "
					: `${isParagraphView ? "." : ""}`
			}`;
		});
	// get all questions as a single line of text.
	const preparedQuestionsAsSingleLine = preparedAllowedQuestionsArray?.join("");
	// return final section output.
	return preparedQuestionsAsSingleLine;
};

export const prepareNoteSingleOrderQuestionSection = ({
	question,
	index = 0,
	depth = 0,
	indentation = 3,
	allowAnswerBesideLabel = false,
}) =>
	`${index ? "\n" : ""}${getIndentationString({ indentation: (depth || 1) - 1 + (indentation - depth || 0) })}- ${
		question?.label
	}: ${[
		...(isTypeSelectText(question?.type || "")
			? question?.answers?.filter(({ id }) => question?.selected_answers?.includes(id))
			: []),
		...(!isTypeSelectText(question?.type || "") ? question?.selected_answers : []),
	]
		.filter(Boolean)
		?.map((item) => `${item?.value || item}.`)
		?.join("")}${
		getOrderAllowedNestedQuestions({ questions: [...(question?.dependency_questions || [])] })?.length
			? `\n${getOrderAllowedNestedQuestions({ questions: [...(question?.dependency_questions || [])] })?.map(
					(depQuestion, depQuestionIndex) =>
						prepareNoteSingleOrderQuestionSection({
							question: depQuestion,
							index: depQuestionIndex,
							depth: depth + 1,
							indentation: indentation + 1,
							allowAnswerBesideLabel,
						})
			  )}`
			: ""
	}`;

export const prepareNoteSingleOrderSection = ({
	order = null,
	indentation = 3,
	depth = 0,
	allowAnswerBesideLabel = false,
}) => [
	...(order !== null
		? [
				...(Array.isArray(order)
					? order
							.map((question, questionIndex) =>
								prepareNoteSingleOrderQuestionSection({
									question,
									index: questionIndex,
									depth,
									indentation,
									allowAnswerBesideLabel,
								})
							)
							.filter(Boolean)
							.join("")
					: Object.keys(order)?.map(
							(orderName, orderNameIndex) =>
								`${
									isOrderChildrenHasAnswers(order[orderName]) &&
									`${
										depth
											? `${orderNameIndex ? "\n" : ""}${getIndentationString({
													indentation: (depth || 1) - 1 + (indentation - depth || 0),
											  })}`
											: ""
									}- ${orderName}\n${prepareNoteSingleOrderSection({
										order: order[orderName],
										indentation: indentation + 1,
										depth: depth + 1,
										allowAnswerBesideLabel,
									})?.join("")}`
								}`
					  )),
		  ]
		: []),
];

export const prepareNoteSections = ({ title = "", content = "", indentation = 0 }) =>
	[...(title ? [title] : []), ...(content ? [content] : [])].join(`\n${getIndentationString({ indentation })}`);

export const prepareNoteSingleEducationInfoSection = ({ info = {}, indentation = 3 }) => {
	const title = `${info?.category ? `${info.category}: ` : ""}${info?.assessment || info?.title || ""}`;
	const content = info?.educational_info || info?.content || "";
	const infoList = [...(info?.group_of_medicines || []), ...(info?.educational_info_items || [])];
	return `- ${title} ${content && ` ${htmlToText(content)}`}${
		infoList?.length
			? `\n${getIndentationString({ indentation })}${prepareNoteEducationalInfoSection({
					info: infoList,
					indentation: indentation + 1,
			  })}`
			: ""
	}`;
};

export const prepareNoteEducationalInfoSection = ({ info = [], title = "", indentation = 2 }) =>
	[
		...(info?.map((item) =>
			prepareNoteSingleEducationInfoSection({
				info: item,
				title,
				indentation: indentation + 1,
			}).replace(/\n\n*/g, "\n")
		) || []),
	].join(`\n${getIndentationString({ indentation })}`);

export const prepareNoteOrdersSection = ({ orders, indentation = 2, allowAnswerBesideLabel = false }) =>
	[
		...(isOrderChildrenHasAnswers(orders)
			? prepareNoteSingleOrderSection({
					order: orders,
					indentation: indentation + 1,
					allowAnswerBesideLabel,
			  }).filter(Boolean)
			: [] || []),
	]
		.filter(Boolean)
		.join(`\n${getIndentationString({ indentation })}`);

export const prepareNoteFollowupSection = ({ note, indentation = 2 }) =>
	[`${note?.followup}`].join(`\n${getIndentationString({ indentation })}`);

export const prepareNoteVisitDurationSection = ({ note, indentation = 2, t }) =>
	[
		`${t("fields.from")} ${note?.visit_duration_from || "-"} ${t("fields.to")} ${note?.visit_duration_to || "-"}`,
	].join(`\n${getIndentationString({ indentation })}`);

export const prepareNoteStatementsSection = ({ note, indentation = 2 }) =>
	[
		...(note.statements?.map(({ title }) =>
			wrapText(`- ${title}`, { indent: getIndentationString({ indentation: indentation + 1 }) }).slice(
				indentation + 4
			)
		) || []),
	].join(`\n${getIndentationString({ indentation })}`);

export const selectNoteMedicinesSingleLongQuestion = ({ type, label }) =>
	isTypeSelectText(type || "") && label?.toLowerCase() === "long";
export const selectNoteMedicinesSingleFrequencyQuestion = ({ type, label }) =>
	(isTypeSelectNumber(type || "") || isTypeSelectNumberUnit(type || "")) && label?.toLowerCase() === "frequency";
export const prepareNoteMedicinesSingleFrequencyQuestion = ({ questions = [] }) =>
	questions.find(selectNoteMedicinesSingleFrequencyQuestion)?.selected_answers?.[0]?.trim() || "";
export const selectNoteMedicinesSingleFrequencyUnitQuestion = ({ type, label }) =>
	isTypeSearchableDropdown(type) && label?.toLowerCase() === "frequency unit";
export const prepareNoteMedicinesSingleFrequencyUnitQuestion = ({ questions = [] }) => {
	const question = questions.find(selectNoteMedicinesSingleFrequencyUnitQuestion);
	const answerIdOrValue = question?.selected_answers?.[0]?.trim() || "";
	const answersList = question?.answers?.filter(
		({ id = "", value = "" }) => answerIdOrValue && [id, value].filter(Boolean).includes(answerIdOrValue)
	);
	return answersList?.[0]?.value || "";
};
export const selectNoteMedicinesSingleDosageQuestion = ({ type, label }) =>
	(isTypeSelectNumber(type || "") || isTypeSelectNumberUnit(type || "")) && label?.toLowerCase() === "dosage";
export const prepareNoteMedicinesSingleDosageQuestion = ({ questions = [] }) =>
	questions.find(selectNoteMedicinesSingleDosageQuestion)?.selected_answers?.[0]?.trim() || "";
export const selectNoteMedicinesSingleDosageTypeQuestion = ({ type, label }) =>
	isTypeSearchableDropdown(type) && label?.toLowerCase() === "dosage type";
export const prepareNoteMedicinesSingleDosageTypeQuestion = ({ questions = [] }) => {
	const question = questions.find(selectNoteMedicinesSingleDosageTypeQuestion);
	const answerIdOrValue = question?.selected_answers?.[0]?.trim() || "";
	const answersList = question?.answers?.filter(
		({ id = "", value = "" }) => answerIdOrValue && [id, value].filter(Boolean).includes(answerIdOrValue)
	);
	return answersList?.[0]?.value || "";
};
export const selectNoteMedicinesSingleDurationQuestion = ({ type, label }) =>
	(isTypeSelectNumber(type || "") || isTypeSelectNumberUnit(type || "")) && label?.toLowerCase() === "duration";
export const prepareNoteMedicinesSingleDurationQuestion = ({ questions = [] }) =>
	questions.find(selectNoteMedicinesSingleDurationQuestion)?.selected_answers?.[0]?.trim() || "";
export const selectNoteMedicinesSingleDurationUnitQuestion = ({ type, label }) =>
	isTypeSearchableDropdown(type) && label?.toLowerCase() === "duration unit";
export const prepareNoteMedicinesSingleDurationUnitQuestion = ({ questions = [] }) => {
	const question = questions.find(selectNoteMedicinesSingleDurationUnitQuestion);
	const answerIdOrValue = question?.selected_answers?.[0]?.trim() || "";
	const answersList = question?.answers?.filter(
		({ id = "", value = "" }) => answerIdOrValue && [id, value].filter(Boolean).includes(answerIdOrValue)
	);
	return answersList?.[0]?.value || "";
};
export const selectNoteMedicinesSingleNoteQuestion = ({ type, label }) =>
	isTypeFreeText(type) && label?.toLowerCase() === "notes";
export const prepareNoteMedicinesSingleNotesQuestion = ({ questions = [] }) =>
	questions.find(selectNoteMedicinesSingleNoteQuestion)?.selected_answers?.[0]?.trim() || "";
export const prepareNoteMedicinesSingleAssessmentsQuestion = ({ assessments = [] }) =>
	assessments
		?.filter(({ general_education = 0 }) => !Boolean(+general_education))
		?.map(({ title }) => title)
		?.join(", ")
		?.trim() || "";
export const prepareNoteMedicinesSingleQuestionLabelPrefix = ({ index = 0, row = [], bullet, asHtml, indentation }) =>
	`${bullet ? `${bullet} ` : ""}${
		row.length >= 2
			? `${(asHtml && "<strong>") || ""}RX${index + 1}:${
					(asHtml && "</strong><br/>") || ""
			  }\n${getIndentationString({
					indentation: indentation + 1,
			  })}`
			: ""
	}`;
export const prepareNoteMedicinesSingleQuestionLabel = ({ label = "", asHtml }) =>
	`${(asHtml && "<strong>") || ""}${label}${(asHtml && "</strong>") || ""}`;
export const prepareNoteMedicinesSingleQuestionSection = ({
	medicine,
	questions = [],
	index = 0,
	row = [],
	bullet,
	indentation,
	asHtml,
}) => {
	const singleQuestionLabelPrefix =
		prepareNoteMedicinesSingleQuestionLabelPrefix({ index, row, bullet, asHtml, indentation }) || "";
	const singleQuestionLabel =
		prepareNoteMedicinesSingleQuestionLabel({ label: medicine?.value || medicine, asHtml }) || "";
	const questionLabel = `${singleQuestionLabelPrefix}${singleQuestionLabel}`;
	const dosageQuestionOutput = prepareNoteMedicinesSingleDosageQuestion({ questions }) || "";
	const dosageTypeQuestionOutput = prepareNoteMedicinesSingleDosageTypeQuestion({ questions }) || "";
	const dosageOutput = `${dosageQuestionOutput} ${dosageTypeQuestionOutput}`.trim();
	const frequencyQuestionOutput = prepareNoteMedicinesSingleFrequencyQuestion({ questions }) || "";
	const frequencyUnitQuestionOutput = prepareNoteMedicinesSingleFrequencyUnitQuestion({ questions }) || "";
	const frequencyOutput = `${frequencyQuestionOutput} ${frequencyUnitQuestionOutput}`.trim();
	const durationQuestionOutput = prepareNoteMedicinesSingleDurationQuestion({ questions }) || "";
	const durationUnitQuestionOutput = prepareNoteMedicinesSingleDurationUnitQuestion({ questions }) || "";
	const durationOutput = `${durationQuestionOutput?.split(" ")?.[0] || ""} ${
		durationUnitQuestionOutput || durationQuestionOutput?.split(" ")?.[1] || ""
	}`.trim();
	const quantityNumber =
		+(dosageQuestionOutput || 1) *
		(+durationQuestionOutput?.split(" ")?.[0] || 1) *
		+(frequencyQuestionOutput || 1);
	// When "Long" is selected, do not show Qty in note output (indefinite duration)
	const longQuestion = questions?.find(selectNoteMedicinesSingleLongQuestion) || null;
	const isLongSelected = (longQuestion?.selected_answers?.length ?? 0) > 0;
	const questionsOutput = [dosageOutput, frequencyOutput, durationOutput].filter(Boolean).join(" X ").trim();
	const notesQuestionOutput = prepareNoteMedicinesSingleNotesQuestion({ questions }) || "";

	// When Long is selected, omit Qty from SIG line (original: always included when quantityNumber was truthy)
	return `${questionLabel ? `${questionLabel}\n${getIndentationString({ indentation: indentation + 1 })}` : ""}${[
		questionsOutput
			? `${(asHtml && "<br/><strong>") || ""}SIG:${(asHtml && "</strong>") || ""} ${[
					questionsOutput,
					...(questionsOutput && quantityNumber && !isLongSelected
						? [`Qty: ${quantityNumber} ${dosageTypeQuestionOutput}`]
						: []),
					// Original: ...(questionsOutput && quantityNumber ? [`Qty: ${quantityNumber} ${dosageTypeQuestionOutput}`] : []),
			  ]
					.filter(Boolean)
					.join(" - ")}`
			: "",
		notesQuestionOutput,
	]
		.filter(Boolean)
		.join(" - ")
		.trim()}`;
};

export const prepareNoteMedicinesSection = ({ note, indentation = 0, asHtml = false }) =>
	[
		...(note?.medicines?.map(({ medicine, questions }, index, row) =>
			prepareNoteMedicinesSingleQuestionSection({
				medicine,
				questions,
				index,
				row,
				bullet: "-",
				indentation: indentation,
				asHtml,
			})
		) || []),
		prepareNoteMedicinesSingleAssessmentsQuestion({ assessments: note?.assessments })
			? [
					`\n${getIndentationString({
						indentation: indentation + 1,
					})}`,
			  ]
			: [],
	].join(`\n\n${getIndentationString({ indentation })}`);

export const prepareNoteSingleProcedureQuestionSection = ({ question = {}, depth = 0, indentation = 0, t }) =>
	`${depth ? `\n${getIndentationString({ indentation: depth + (indentation - 1 || 0) })}` : ""}${`- ${
		question?.label
	}${question?.set_as_not_examined ? ` (${t("actions.not_examined")}) ` : ""}${
		((Array.isArray(question?.selected_answers) && question?.selected_answers?.length) ||
			typeof question?.selected_answers === "string") &&
		!question?.output_text &&
		!question?.normal_text &&
		!question?.abnormal_text
			? `: ${
					Array.isArray(question?.selected_answers)
						? question?.answers
								?.filter(({ id }) => question?.selected_answers?.includes(id))
								?.map(({ value }) => value)
								?.join(" - ") || question?.selected_answers?.join(" - ")
						: question?.selected_answers || ""
			  }.`
			: ""
	}`}${
		!question?.set_as_not_examined
			? `${
					getOrderAllowedNestedQuestions({
						questions: [...(question?.dependency_questions || []), ...(question?.children || [])],
					})?.length
						? `${getOrderAllowedNestedQuestions({
								questions: [...(question?.dependency_questions || []), ...(question?.children || [])],
						  })
								?.map((question, index) =>
									prepareNoteSingleProcedureQuestionSection({
										question,
										indentation: indentation + 1,
										depth: depth + 1,
										index,
										t,
									})
								)
								.join("")}`
						: ""
			  }`
			: ""
	}`;

export const prepareNoteOfficeProceduresSection = ({ note, indentation = 2, t, user }) =>
	note?.procedures
		?.filter(
			({ type, question }) =>
				type?.toLowerCase() === "statement" ||
				(type?.toLowerCase() === "question" &&
					((isTypeGroup(question?.type || "") && question?.children?.length) ||
						(Array.isArray(question?.selected_answers) && question?.selected_answers?.length) ||
						Boolean(question?.selected_answers)))
		)
		?.map(({ type, question, label }, index) =>
			type?.toLowerCase() === "question"
				? prepareNoteSingleProcedureQuestionSection({
						question,
						user,
						indentation,
						index,
						t,
				  })
				: `- ${t("titles.statement")}: ${label}`
		)
		.join(`\n${getIndentationString({ indentation })}`);

export const prepareNotePlanSections = ({
	note,
	t,
	indentation = 1,
	withTrackingData = false,
	isPharmacySegment = false,
	user,
	allowAnswerBesideLabel,
	allowHTML,
	treeView,
}) => {
	const noteEducationalInfo = [...(note?.educational_info_items || []), ...(note?.group_of_medicines || [])];
	const noteEducationalInfoGroupByAssessmentId = groupBy(noteEducationalInfo, "assessment_id");

	return [
		...(note?.procedures?.filter(
			({ type, question }) =>
				type?.toLowerCase() === "statement" ||
				(type?.toLowerCase() === "question" &&
					((isTypeGroup(question?.type || "") && question?.children?.length) ||
						(Array.isArray(question?.selected_answers) && question?.selected_answers?.length) ||
						Boolean(question?.selected_answers)))
		)?.length
			? [
					{
						title: `${t("titles.office_procedures")}:`,
						content: `${getIndentationString({ indentation })}${prepareNoteOfficeProceduresSection({
							note,
							indentation: indentation + 1,
							withTrackingData,
						})}`,
						indentation,
					},
			  ]
			: []),
		...(noteEducationalInfo?.length
			? [
					{
						title: `${t("titles.educational_info")}:`,
						content: `${getIndentationString({ indentation })}${prepareNoteEducationalInfoSection({
							info: Object.keys(noteEducationalInfoGroupByAssessmentId).map((assessment_id) => {
								return noteEducationalInfoGroupByAssessmentId?.[assessment_id].reduce(
									(acc, curr) => Object.assign(acc, curr),
									{}
								);
							}),
							indentation: indentation + 1,
						})}`,
						indentation,
					},
			  ]
			: []),
		...(note?.medicines?.length
			? [
					{
						title: `${t("titles.medication_history")}:`,
						content: `${getIndentationString({ indentation })}${prepareNoteMedicinesSection({
							note,
							indentation: indentation + 1,
						})}`,
						indentation,
					},
			  ]
			: []),
		...(note?.statements?.length
			? [
					{
						title: `${t(isPharmacySegment ? "titles.pharmacists_comments" : "titles.doctors_comments")}:`,
						content: `${getIndentationString({ indentation })}${prepareNoteStatementsSection({
							note,
							indentation: indentation + 1,
						})}`,
						indentation,
					},
			  ]
			: []),
		...(Object.keys(note?.orders || {})?.length
			? [
					{
						title: `${t("titles.orders")}:`,
						content: `${getIndentationString({ indentation })}${prepareNoteOrdersSection({
							orders: { ...(prepareOrdersWithRepeatedQuestions({ orders: note?.orders }) || {}) },
							indentation: indentation + 1,
							allowAnswerBesideLabel: true,
						})}`,
						indentation,
					},
			  ]
			: []),
		...(note?.followup
			? [
					{
						title: ``,
						content: `${t("titles.followup")}:${getIndentationString({
							indentation,
						})}${prepareNoteFollowupSection({
							note,
							indentation: indentation + 1,
						})}`,
						indentation,
					},
			  ]
			: []),
		...(note?.visit_duration_from || note?.visit_duration_to
			? [
					{
						title: `${t("titles.visit_duration")}:`,
						content: `${getIndentationString({ indentation })}${prepareNoteVisitDurationSection({
							note,
							indentation: indentation + 1,
							t,
						})}`,
						indentation,
					},
			  ]
			: []),
		...(getAllowedNestedQuestions({
			questions: [
				...(prepareDependenciesQuestionsWithRepeatedOnes({
					questions: [...(sortQuestionsByAPIOrder({ questions: note?.questions?.plan || [] }) || [])],
				}) || []),
			],
			section: vars.app.soapSections.plan,
		})?.length
			? [
					{
						title: "",
						content: `${getIndentationString({
							indentation: indentation - 1,
						})}${prepareNoteQuestionsSection({
							questions: getAllowedNestedQuestions({
								questions: [
									...(prepareDependenciesQuestionsWithRepeatedOnes({
										questions: [
											...(sortQuestionsByAPIOrder({ questions: note?.questions?.plan || [] }) ||
												[]),
										],
									}) || []),
								],
								section: vars.app.soapSections.plan,
							}),
							indentation: indentation,
							user,
							t,
							withTrackingData,
							allowAnswerBesideLabel,
							section: vars.app.soapSections.plan,
							isPharmacySegment,
							treeView,
							allowHTML,
						})}`,
						indentation,
					},
			  ]
			: []),
	]
		.map((item) => prepareNoteSections(item))
		.join(`\n\n${getIndentationString({ indentation })}`);
};

export const prepareNoteAssessmentsSection = ({ note, indentation = 2 }) =>
	[
		...(note?.assessments
			?.filter(({ general_education = 0 }) => !Boolean(+general_education))
			?.map(({ title }) =>
				wrapText(`- ${title}`, { indent: getIndentationString({ indentation: indentation + 1 }) }).slice(
					indentation + 3
				)
			) || []),
	].join(`\n${getIndentationString({ indentation })}`);

export const prepareNoteHeaderSection = ({
	note,
	t,
	isDemo,
	withTrackingData = false,
	indentation = 0,
	locationState = {},
}) =>
	[
		{
			label: t("titles.patient_name"),
			value: isDemo ? "Olive Yew" : formatNotePatientFullName(note, locationState) || "-",
		},
		{ label: t("titles.note_id"), value: note?.short_id || "" },
		...(withTrackingData
			? [
					...(note?.user?.name ? [{ label: t("titles.creator"), value: note.user.name }] : []),
					...(note?.created_at?.date || note?.start_at?.date
						? [{ label: t("titles.start_date"), value: note?.created_at?.date || note.start_at.date }]
						: []),
					...(note?.created_at?.time || note?.start_at?.time
						? [{ label: t("titles.start_time"), value: note?.created_at?.time || note.start_at.time }]
						: []),
					...(note?.ended_at?.date ? [{ label: t("titles.end_date"), value: note.ended_at.date }] : []),
					...(note?.ended_at?.time ? [{ label: t("titles.end_time"), value: note.ended_at.time }] : []),
					...(note?.systems?.map(({ title }) => title)?.length
						? [{ label: t("titles.systems"), value: note.systems.map(({ title }) => title).join(" - ") }]
						: []),
			  ]
			: []),
		...(note?.cases?.map(({ title }) => title)?.length
			? [
					{
						label: t("titles.cases"),
						value: note.cases.map(({ title }) => title).join(" - "),
					},
			  ]
			: []),
		{ label: t("titles.gender"), value: note?.patient_plain_text?.gender || note?.gender || "-" },
		{
			label: t("titles.date_of_birth"),
			value: isDemo
				? "July 1 1970"
				: `${
						(note?.patient_plain_text?.date_of_birth &&
							generateDateOfBirthForNoteOutput(note?.patient_plain_text?.date_of_birth)) ||
						"-"
				  }.`,
		},
	]
		?.map((trackData) => `${trackData?.label}: ${trackData?.value}`)
		?.join(`\n${getIndentationString({ indentation })}`);

export const prepareNoteFooterSection = ({ t }) =>
	wrapText(t("titles.note_output_footer", { brand: vars.app.name }), { indent: "" });

export const wrapNoteText = (noteText = "") =>
	flattenDeep(
		noteText.split("\n")?.map((item) => {
			if (item?.length <= MAX_LINE_WIDTH) return [item];
			const itemStartTrimmed = item?.trimStart();
			const startTrimmedSubString = item?.replace(itemStartTrimmed, "");
			const itemEndTrimmed = item?.trimEnd();
			const EndTrimmedSubString = item?.replace(itemEndTrimmed, "");
			const chunks = splitSentence(
				item,
				MAX_LINE_WIDTH - ((startTrimmedSubString?.length || 0) + (EndTrimmedSubString?.length || 0))
			);
			return chunks?.map(
				(chunk) =>
					`${getIndentationString({
						indentation: startTrimmedSubString.length,
						indentationCharacter: " ",
					})}${chunk?.trim()}${getIndentationString({
						indentation: EndTrimmedSubString.length,
						indentationCharacter: " ",
					})}`
			);
		})
	)?.join("\n");

export const preparePatientHistoryText = ({
	basicQuestions = [],
	withTrackingData = false,
	t,
	user,
	indentation = 0,
	allowLabel = false,
	isPharmacySegment = false,
	allowHTML,
	allowWrap,
	treeView = false,
}) => {
	let finalOutput = [
		...(getAllowedNestedQuestions({ questions: [...(basicQuestions || [])], isPatientHistory: true })?.length
			? [
					{
						content: `${getIndentationString({
							indentation: indentation + 1,
						})}${prepareNoteQuestionsSection({
							questions: basicQuestions || [],
							indentation: indentation + 1,
							user,
							t,
							withTrackingData,
							allowLabel,
							isPharmacySegment,
							isPatientHistory: true,
							treeView,
							allowHTML,
						})}`,
					},
			  ]
			: []),
	]
		.map((item) => prepareNoteSections(item))
		.join(
			allowHTML
				? "<hr/>"
				: `\n${getIndentationString({ indentation: MAX_LINE_WIDTH, indentationCharacter: "-" })}\n`
		);
	if (allowWrap) finalOutput = wrapNoteText(finalOutput);

	return finalOutput;
};

export const SOAP_NOTE_OUTPUT_SECTION_IDS = [
	vars.app.soapSections.subjective,
	vars.app.soapSections.objective,
	vars.app.soapSections.assessments,
	vars.app.soapSections.plan,
];

export const buildNoteTextSectionItems = ({
	note,
	withTrackingData = false,
	t,
	user,
	indentation = 0,
	allowLabel = false,
	isPharmacySegment = false,
	isPatientLocationCentric,
	language,
	allowHTML,
	treeView = false,
}) => {
	const bcProvinceForSubjectiveOutput = getEffectiveProvinceForBcScreening({
		isPatientLocationCentric,
		accountHolderState: user?.state,
		patientState: note?.patient_plain_text?.state || note?.patient_plain_text?.province,
	});
	const showBcMinorAilmentSubjectiveLines =
		isCountryCanada(user?.country || "") &&
		isPharmacySegment &&
		isBritishColumbiaProvince(bcProvinceForSubjectiveOutput);
	return [
		...(getAllowedNestedQuestions({
			questions: [
				...(prepareDependenciesQuestionsWithRepeatedOnes({
					questions: [
						...(sortQuestionsByAPIOrder({
							questions: [...(note?.questions?.subjective || [])],
						}) || []),
					],
				}) || []),
			],
		})?.length
			? [
					{
						id: vars.app.soapSections.subjective,
						title: `${t("titles.subjective")}:`,
						content: `${getIndentationString({ indentation: indentation + 1 })}${t(
							"system_messages.a_pleasant_age_gender_patient_was_assessed_on_date",
							{
								age: ` ${
									(note?.patient_plain_text?.date_of_birth &&
										formatAgeForNoteOutput(
											calculateAgeFromBirthDate(note?.patient_plain_text?.date_of_birth) || {},
											{
												yearFormat: t("titles.year_symbol"),
												monthFormat: t("titles.month_symbol"),
												dayFormat: t("titles.day_symbol"),
												allowProperNoun: false,
											}
										)) ||
									"-"
								}`,
								gender: `${
									["male", "female"]?.includes(
										(note?.patient_plain_text?.gender || note?.gender || "-")?.toLowerCase()
									)
										? ` ${(note?.patient_plain_text?.gender || note?.gender || "-")?.toLowerCase()}`
										: ""
								} `,
								date: moment(note?.ended_at?.date, "DD/MM/YYYY")
									.locale(language)
									.format("dddd, MMMM DD, YYYY"),
								// HOLD: add booking_time to date
								// (() => {
								// 	// Try multiple date sources with backward compatibility
								// 	// 1. ended_at.date (normal completed notes)
								// 	// 2. start_at.date (scheduled appointments/JustMeds)
								// 	// 3. created_at.date (fallback to note creation date)
								// 	const dateStr =
								// 		note?.ended_at?.date || note?.start_at?.date || note?.created_at?.date;
								// 	const dateMoment = moment(dateStr, "DD/MM/YYYY");
								// 	// Return formatted date if valid, otherwise return current date as fallback
								// 	return dateMoment.isValid()
								// 		? dateMoment.locale(language).format("dddd, MMMM DD, YYYY")
								// 		: moment().locale(language).format("dddd, MMMM DD, YYYY");
								// })(),

								visit:
									note?.visit_type_source === "store" && (note?.visit_type || note?.visit_recurrence)
										? ` in ${
												note?.visit_type
													? `${
															note.visit_type === "in_person"
																? "an in person"
																: "a virtual"
													  }`
													: ""
										  }${note?.visit_recurrence ? ` ${note?.visit_recurrence}` : ""} visit`
										: "",
							}
						)}${
							isCountryCanada(user?.country || "") && isPharmacySegment
								? `${
										note?.patient_consented
											? `\n${getIndentationString({
													indentation: indentation + 1,
											  })}${t(
													"system_messages.informed_consent_to_issue_minor_ailments_prescription_has_been_obtained"
											  )}.`
											: ""
								  }${
										isStateNewBrunswick(user?.state || "")
											? `${
													note?.nb_no_correctional_facility
														? `\n${getIndentationString({
																indentation: indentation + 1,
														  })}${t("system_messages.nb_no_correctional_facility")}`
														: ""
											  }${
													note?.nb_no_nursing_home
														? `\n${getIndentationString({
																indentation: indentation + 1,
														  })}${t("system_messages.nb_no_nursing_home")}`
														: ""
											  }${
													note?.nb_valid_medicare
														? `\n${getIndentationString({
																indentation: indentation + 1,
														  })}${t("system_messages.nb_valid_medicare")}`
														: ""
											  }`
											: ""
								  }${
										showBcMinorAilmentSubjectiveLines
											? `${
													note?.pharmanet_check
														? `\n${getIndentationString({
																indentation: indentation + 1,
														  })}${t("system_messages.pharma_net_checked")}`
														: ""
											  }${
													note?.patient_eligible
														? `\n${getIndentationString({
																indentation: indentation + 1,
														  })}${t("system_messages.patient_is_eligible")}`
														: ""
											  }`
											: ""
								  }\n`
								: ""
						}\n${getIndentationString({
							indentation: indentation + 1,
						})}${prepareNoteQuestionsSection({
							questions:
								prepareDependenciesQuestionsWithRepeatedOnes({
									questions: [
										...(sortQuestionsByAPIOrder({
											questions: [...(note?.questions?.subjective || [])],
										}) || []),
									],
								}) || [],
							indentation: indentation + 1,
							user,
							t,
							withTrackingData,
							allowLabel,
							section: vars.app.soapSections.subjective,
							isPharmacySegment,
							treeView,
							allowHTML,
						})}`,
					},
			  ]
			: []),
		...(getAllowedNestedQuestions({
			questions: [
				...(prepareDependenciesQuestionsWithRepeatedOnes({
					questions: [...(note?.questions?.objective || [])],
				}) || []),
			],
		})?.length
			? [
					{
						id: vars.app.soapSections.objective,
						title: `${t("titles.objective")}:`,
						content: `${getIndentationString({
							indentation: indentation + 1,
						})}${prepareNoteQuestionsSection({
							questions:
								prepareDependenciesQuestionsWithRepeatedOnes({
									questions: [...(note?.questions?.objective || [])],
								}) || [],
							indentation: indentation + 1,
							user,
							t,
							withTrackingData,
							allowLabel,
							section: vars.app.soapSections.objective,
							isPharmacySegment,
							treeView,
							allowHTML,
						})}`,
					},
			  ]
			: []),
		...(note?.assessments?.filter(({ general_education = 0 }) => !Boolean(+general_education))?.length
			? [
					{
						id: vars.app.soapSections.assessments,
						title: `${t("titles.assessments")}:`,
						content: `${getIndentationString({
							indentation: indentation + 1,
						})}${prepareNoteAssessmentsSection({
							note,
							indentation: indentation + 1,
						})}`,
					},
			  ]
			: []),
		...(note?.procedures?.filter(
			({ type, question }) =>
				type?.toLowerCase() === "statement" ||
				(type?.toLowerCase() === "question" &&
					((isTypeGroup(question?.type || "") && question?.children?.length) ||
						(Array.isArray(question?.selected_answers) && question?.selected_answers?.length) ||
						Boolean(question?.selected_answers)))
		)?.length ||
		note?.educational_info_items?.length ||
		note?.group_of_medicines?.length ||
		note?.medicines?.length ||
		note?.statements?.length ||
		Object.keys(note?.orders || {})?.length ||
		note?.followup ||
		note?.visit_duration_from ||
		note?.visit_duration_to ||
		getAllowedNestedQuestions({
			questions: [
				...(prepareDependenciesQuestionsWithRepeatedOnes({
					questions: [...(sortQuestionsByAPIOrder({ questions: note?.questions?.plan || [] }) || [])],
				}) || []),
			],
			section: vars.app.soapSections.plan,
		})?.length
			? [
					{
						id: vars.app.soapSections.plan,
						title: `${t("titles.plan")}:`,
						content: `${getIndentationString({ indentation: indentation + 1 })}${prepareNotePlanSections({
							note,
							t,
							withTrackingData,
							isPharmacySegment,
							user,
							allowAnswerBesideLabel: true,
							treeView,
							allowHTML,
						})}`,
					},
			  ]
			: []),
	];
};

export const buildNoteTextSections = (options = {}) =>
	buildNoteTextSectionItems(options)
		.map(({ id, title, content }) => ({
			id,
			title,
			text: prepareNoteSections({ title, content, indentation: options.indentation ?? 0 }),
		}))
		.filter(({ id, text }) => id && text?.trim());

export const prepareNoteSectionText = ({ sectionId, ...options }) => {
	const section = buildNoteTextSections({ ...options, allowHTML: false }).find(({ id }) => id === sectionId);
	return section?.text?.trim() || "";
};

export const prepareNoteText = ({
	note,
	isDemo,
	withTrackingData = false,
	t,
	user,
	indentation = 0,
	allowLabel = false,
	isPharmacySegment = false,
	isPatientLocationCentric,
	language,
	allowHeaderData = true,
	allowHTML,
	allowWrap,
	treeView = false,
	locationState = {},
}) => {
	let finalOutput = [
		...(allowHeaderData
			? [
					{
						content: `${
							allowHTML
								? "<strong style='font-size: 1rem; font-weight: 500; text-transform: capitalize;'>\n"
								: ""
						}${prepareNoteHeaderSection({ note, withTrackingData, t, isDemo, locationState })}${
							allowHTML ? "\n</strong>" : ""
						}`.trim(),
					},
			  ]
			: []),
		...buildNoteTextSectionItems({
			note,
			withTrackingData,
			t,
			user,
			indentation,
			allowLabel,
			isPharmacySegment,
			isPatientLocationCentric,
			language,
			allowHTML,
			treeView,
		}),
		{ content: prepareNoteFooterSection({ t }) },
	]
		.map((item) => prepareNoteSections(item))
		.join(
			allowHTML
				? "<hr/>"
				: `\n${getIndentationString({ indentation: MAX_LINE_WIDTH, indentationCharacter: "-" })}\n`
		);
	if (allowWrap) finalOutput = wrapNoteText(finalOutput);

	return finalOutput;
};

export const searchForNodeInTreeAndReplace = ({ list = [], single = {}, allowFindByIndex = false }) => [
	...(list.map((singleItem) => {
		if (
			(allowFindByIndex &&
				single?.index &&
				singleItem?.index &&
				singleItem?.index === single?.index &&
				singleItem?.id === single?.id) ||
			(allowFindByIndex && (!single?.index || !singleItem?.index) && singleItem?.id === single?.id) ||
			(!allowFindByIndex && singleItem?.id === single?.id)
		)
			return { ...(single || {}), ...(singleItem?.sort_order ? { sort_order: singleItem.sort_order } : {}) };
		if (
			singleItem?.hasOwnProperty("dependency_questions") &&
			Array.isArray(singleItem?.dependency_questions) &&
			singleItem?.dependency_questions?.length
		)
			return {
				...(singleItem || {}),
				dependency_questions: searchForNodeInTreeAndReplace({
					list: [...(singleItem?.dependency_questions || [])],
					single: { ...(single || {}) },
					allowFindByIndex,
				}),
			};
		if (
			singleItem?.hasOwnProperty("children") &&
			Array.isArray(singleItem?.children) &&
			singleItem?.children?.length
		)
			return {
				...(singleItem || {}),
				children: searchForNodeInTreeAndReplace({
					list: [...(singleItem?.children || [])],
					single: { ...(single || {}) },
					allowFindByIndex,
				}),
			};
		return singleItem;
	}) || []),
];

export const getPathToNode = (data, targetId, targetKey = "id") => {
	const findPath = (node, path = []) => {
		if (node?.[targetKey] === targetId) return path.concat(node);

		if (Array.isArray(node)) {
			for (let i = 0; i < node.length; i++) {
				const subPath = findPath(node[i], path.concat([i]));
				if (subPath) return subPath;
			}
		} else if (typeof node === "object") {
			for (const key in node) {
				if (Object.hasOwnProperty.call(node, key)) {
					const subPath = findPath(node[key], path.concat([key]));
					if (subPath) return subPath;
				}
			}
		}

		return null;
	};

	return findPath(data, []);
};

export const getNestedQuestionLogs = ({ question = {} }) => {
	const { log = {}, dependencies = [], children = [], ...restOfQuestion } = question;

	return flattenDeep([
		...(log && Object.keys(log)?.length ? [{ log, ...restOfQuestion }] : []),
		...(dependencies?.length
			? dependencies.map((depQuestion) =>
					getNestedQuestionLogs({ question: { ...(depQuestion || {}), exactParent: question } })
			  )
			: []),
		...(children?.length
			? children.map((childQuestion) =>
					getNestedQuestionLogs({ question: { ...(childQuestion || {}), exactParent: question } })
			  )
			: []),
	]);
};

export const getNumberQuestionSelectedAnswer = (question = {}, placeholder = 0) =>
	`${
		question?.digits && +question.digits > 0
			? Array.apply(null, Array(+question.digits))
					.map(() => placeholder)
					?.join("")
			: placeholder
	}${
		question?.decimal_digits && +question.decimal_digits > 0
			? `.${Array.apply(null, Array(+question.decimal_digits))
					.map(() => placeholder)
					?.join("")}`
			: ""
	}`;

export const getOrderAllowedNestedQuestions = ({ questions, allowDepQuestions = true }) => [
	...(questions?.filter(
		({
			dependency_questions = [],
			children = [],
			set_as_not_examined = null,
			answers = [],
			selected_answers = null,
		}) =>
			[
				...(Array.isArray(selected_answers) && selected_answers?.length
					? answers?.length
						? answers.filter(({ id }) => selected_answers.includes(id))
						: selected_answers
					: typeof selected_answers === "string"
					? [selected_answers]
					: []),
				...(allowDepQuestions ? getOrderAllowedNestedQuestions({ questions: dependency_questions }) || [] : []),
				...(getOrderAllowedNestedQuestions({ questions: children }) || []),
			].filter(Boolean).length || set_as_not_examined
	) || []),
];

export const getQuestionsWithNestedOnes = ({ questions = [], excludeKeyword = "" }) => [
	...(questions?.map(({ id, dependency_questions: _dependency_questions, children, type, keyword }) => {
		const isTypeAllowed = ![
			vars.app.questionTypes.group,
			vars.app.questionTypes.freeText,
			vars.app.questionTypes.searchableDropdown,
		]?.includes(type?.toLowerCase());
		const isKeywordAllowed =
			!excludeKeyword ||
			(excludeKeyword && !isQuestionHaveKeyword({ question: { keyword }, keyword: excludeKeyword }));

		return [
			...(isTypeAllowed && isKeywordAllowed ? [id] : []),
			...(isKeywordAllowed
				? [
						// ...(getQuestionsWithNestedOnes({ questions: dependency_questions || [], excludeKeyword }) || []),
						...(getQuestionsWithNestedOnes({ questions: children || [], excludeKeyword }) || []),
				  ]
				: []),
		];
	}) || []),
];

export const getQuestionAnswer = ({ list = [], id = "", exclude = [] }) =>
	list.map((singleItem) => {
		if (
			singleItem?.id === id &&
			(!exclude.length || (exclude.length && !exclude?.includes(singleItem?.type?.toLowerCase())))
		)
			return Array.isArray(singleItem?.selected_answers) && singleItem?.selected_answers?.length
				? singleItem?.answers?.length
					? singleItem?.answers
							.filter(({ id }) => singleItem?.selected_answers.includes(id))
							?.map(({ value }) => value)
					: singleItem?.selected_answers
				: typeof singleItem?.selected_answers === "string"
				? [singleItem?.selected_answers]
				: [];
		if (
			singleItem?.hasOwnProperty("dependency_questions") &&
			Array.isArray(singleItem?.dependency_questions) &&
			singleItem?.dependency_questions?.length
		)
			return getQuestionAnswer({ list: singleItem?.dependency_questions, id });
		if (
			singleItem?.hasOwnProperty("children") &&
			Array.isArray(singleItem?.children) &&
			singleItem?.children?.length
		)
			return getQuestionAnswer({ list: singleItem?.children, id });

		return [];
	});

export const prepareNoteShareLinkText = ({ link, otp }) => [`Link: ${link}`, `Pass Code: ${otp}`].join("\n");

export const prepareQuestionsWithNestedQuestions = ({ questions, depth = 0, allowPatient = false }) =>
	flattenDeep([
		...([...(allowPatient ? questions?.filter(({ patient }) => Boolean(patient)) : questions || [])]?.map(
			({ id, dependency_questions = [], children = [] }) => [
				id,
				...(dependency_questions?.length
					? prepareQuestionsWithNestedQuestions({
							questions: dependency_questions || [],
							depth: depth + 1,
							allowPatient: allowPatient,
					  })
					: []),
				...(children?.length
					? prepareQuestionsWithNestedQuestions({
							questions: children || [],
							depth: depth + 1,
							allowPatient: allowPatient,
					  })
					: []),
			]
		) || []),
	]);

export const sortQuestionsByAPIOrder = ({ questions, depth = 0 }) =>
	[...(questions || [])].map(({ dependency_questions = [], children = [], ...singleQuestion }, index) => ({
		...singleQuestion,
		sort_order: index + 1,
		...(dependency_questions?.length
			? {
					dependency_questions: sortQuestionsByAPIOrder({
						questions: dependency_questions || [],
						depth: depth + 1,
					}),
			  }
			: []),
		...(children?.length
			? { children: sortQuestionsByAPIOrder({ questions: children || [], depth: depth + 1 }) }
			: []),
	}));

export const getNoteSection = (pathname = "") =>
	((pathname?.split("/")?.length > 0 && pathname.split("/")[pathname.split("/").length - 1]) || "")?.toLowerCase();

export const isTypeGroup = (type = "") => type === vars.app.questionTypes.group || false;
export const isTypeSelectText = (type = "") => type === vars.app.questionTypes.selectText || false;
export const isTypeSelectTextIcon = (type = "") => type === vars.app.questionTypes.selectTextIcon || false;
export const isTypeSelectTextImage = (type = "") => type === vars.app.questionTypes.selectTextImage || false;
export const isTypeSelectNumber = (type = "") => type === vars.app.questionTypes.selectNumber || false;
export const isTypeSelectNumberUnit = (type = "") => type === vars.app.questionTypes.selectNumberUnit || false;
export const isTypeSelectTwoNumbers = (type = "") => type === vars.app.questionTypes.selectTwoNumbers || false;
export const isTypeSearchableDropdown = (type = "") => type === vars.app.questionTypes.searchableDropdown || false;
export const isTypeFreeText = (type = "") => type === vars.app.questionTypes.freeText || false;
export const isTypeDate = (type = "") => type === vars.app.questionTypes.date || false;
export const isTypeTime = (type = "") => type === vars.app.questionTypes.time || false;
export const isTypeDiagram = (type = "") => type === vars.app.questionTypes.diagram || false;
export const isTypePatientSignature = (type = "") => type === vars.app.questionTypes.patientSignature || false;
export const isAnswerSourceCustomList = (answer_source = "") => answer_source === "custom_list" || false;

/**
 * A definition (question/answer `description`) shown to a patient must never reveal clinician-only
 * "red flag" wording. This guard applies to every presentation style — inline especially, since it
 * is always visible rather than hidden behind a hover.
 */
export const canShowDefinition = ({ description = "", isAuthenticated = false } = {}) => {
	if (!description) return false;
	if (isAuthenticated) return true;
	return String(description).toLowerCase().indexOf("red flag") < 0;
};

export const isDefinitionViewStyleInline = (definitionViewStyle = "") =>
	String(definitionViewStyle || "").toLowerCase() === vars.app.definitionViewStyles.inline;

export const shouldShowDefinitionAsTooltip = ({
	description = "",
	isAuthenticated = false,
	definitionViewStyle = "",
} = {}) => canShowDefinition({ description, isAuthenticated }) && !isDefinitionViewStyleInline(definitionViewStyle);

export const shouldShowDefinitionInline = ({
	description = "",
	isAuthenticated = false,
	definitionViewStyle = "",
} = {}) => canShowDefinition({ description, isAuthenticated }) && isDefinitionViewStyleInline(definitionViewStyle);

export const calculateQuestionSelectedAnswer = ({
	question = {},
	questionAnswers = [],
	isQuestionAnswerSourceCustomList = false,
}) => {
	const questionType = question?.type || "";
	const questionSelectedAnswers = [...(question?.selected_answers || [])];
	const normalizedSelectedAnswerKeys = questionSelectedAnswers
		.map((item) => String((isObject(item) ? item?.id || item?.value || "" : item) || ""))
		.filter(Boolean);
	const questionAnswersFiltered = [
		...(questionAnswers?.filter((answer) => normalizedSelectedAnswerKeys.includes(String(answer?.id ?? ""))) || []),
	];
	const isQuestionTypeSelectText =
		isTypeSelectText(questionType) || isTypeSelectTextIcon(questionType) || isTypeSelectTextImage(questionType);
	const isQuestionTypeSearchableDropdown = isTypeSearchableDropdown(questionType);
	const isQuestionTypeDiagram = isTypeDiagram(questionType);
	const isQuestionTypeSelectNumber = isTypeSelectNumber(questionType);
	const isQuestionTypeSelectNumberUnit = isTypeSelectNumberUnit(questionType);
	const isQuestionTypeSelectTwoNumbers = isTypeSelectTwoNumbers(questionType);
	const isQuestionTypeDate = isTypeDate(questionType);
	const isQuestionTypeTime = isTypeTime(questionType);
	const digits = question?.digits || "";
	const decimal_digits = question?.decimal_digits || "";
	const questionSelectedAnswersFirstElement =
		(isObject(questionSelectedAnswers?.[0]) &&
			(questionSelectedAnswers?.[0]?.id || questionSelectedAnswers?.[0]?.value || "")) ||
		questionSelectedAnswers?.[0] ||
		"";
	const questionSelectedAnswersSecondElement =
		(isObject(questionSelectedAnswers?.[1]) &&
			(questionSelectedAnswers?.[1]?.id || questionSelectedAnswers?.[1]?.value || "")) ||
		questionSelectedAnswers?.[1] ||
		"";
	const isQuestionSelectedAnswersFirstElementFoundInOutputTextException = vars.app.questionOutputTextException?.some(
		(item) => item.toLowerCase().indexOf(questionSelectedAnswersFirstElement?.toLowerCase()) >= 0
	);

	return [
		...(isQuestionTypeSelectText ||
		(isQuestionTypeSearchableDropdown && isQuestionAnswerSourceCustomList) ||
		isQuestionTypeDiagram
			? questionAnswersFiltered
			: isQuestionTypeSearchableDropdown
			? normalizedSelectedAnswerKeys.map((item) => {
					if (isObject(item)) return item;
					const normalizedItem = String(item);
					return (
						questionAnswers?.find(
							(answer) =>
								String(answer?.id ?? "") === normalizedItem ||
								String(answer?.value ?? "") === normalizedItem
						) || {
							id: item,
							value: item,
						}
					);
			  })
			: isQuestionTypeSelectNumber || isQuestionTypeSelectNumberUnit || isQuestionTypeSelectTwoNumbers
			? questionSelectedAnswers?.length
				? [
						...(isQuestionTypeSelectNumberUnit &&
						isQuestionSelectedAnswersFirstElementFoundInOutputTextException
							? [
									getNumberQuestionSelectedAnswer({ digits, decimal_digits }),
									questionSelectedAnswersFirstElement,
							  ]
							: isQuestionTypeSelectTwoNumbers
							? [questionSelectedAnswersFirstElement, questionSelectedAnswersSecondElement]
							: [
									questionSelectedAnswersFirstElement?.split(" ")?.slice(0, 1)?.join(" ")?.trim(),
									questionSelectedAnswersFirstElement?.split(" ")?.slice(1)?.join(" ")?.trim(),
							  ]),
				  ]
				: [
						getNumberQuestionSelectedAnswer({ digits, decimal_digits }),
						isQuestionTypeSelectTwoNumbers
							? getNumberQuestionSelectedAnswer({ digits, decimal_digits })
							: "",
				  ]
			: isQuestionTypeDate || isQuestionTypeTime || isTypePatientSignature(questionType)
			? questionSelectedAnswers || [""]
			: ["", ""]),
	];
};

export const prepareImageMapperOptions = ({
	id = "",
	selectedAnswers = [],
	section = "",
	answersWithCoordinates = [],
}) => ({
	name: `image-map-${id}`,
	areas: [
		...(answersWithCoordinates?.map(({ coordinates, id, shape, value: name }) => {
			const coords = [...(coordinates?.split(",")?.map(Number) || [])];
			const polygon = [
				...(coords?.reduce((resultArray, item, index) => {
					const chunkIndex = Math.floor(index / 2);
					if (!resultArray[chunkIndex]) resultArray[chunkIndex] = [];
					resultArray[chunkIndex].push(+item);
					return resultArray;
				}, []) || []),
			];
			const documentElement = document.documentElement;
			const strokeColor = getNodeCSSPropertyValue(documentElement, "--bs-primary");
			const preFillColor = `${(() => {
				const highlightedAnswers = selectedAnswers?.includes(id);
				return `rgba(${getNodeCSSPropertyValue(
					documentElement,
					`--bs-${section}${highlightedAnswers ? "-400" : ""}-rgb`
				)}, ${highlightedAnswers ? "0.75" : "0.5"})`;
			})()}`;

			return { id, name, polygon, coords, shape, strokeColor, preFillColor };
		}) || []),
	],
});

export const getSetAnswerValueAttributeBasedOnQuestionType = ({ type, answer_source }) =>
	isTypeSelectText(type) ||
	isTypeSelectTextIcon(type) ||
	isTypeSelectTextImage(type) ||
	isTypeDiagram(type) ||
	(isTypeSearchableDropdown(type) && isAnswerSourceCustomList(answer_source))
		? "answer_ids"
		: "values";

export const formatAgeForNoteOutput = (
	age = {},
	options = { yearFormat: "Year", monthFormat: "Month", dayFormat: "Day", allowProperNoun: true }
) => {
	const years = age?.years || 0;
	const months = age?.months || 0;
	const days = age?.days || 0;

	return [
		...(years > 0 ? [`${years}${options?.yearFormat}${options?.allowProperNoun && years > 1 ? "s" : ""}`] : []),
		...(months > 0 ? [`${months}${options?.monthFormat}${options?.allowProperNoun && months > 1 ? "s" : ""}`] : []),
		...(days > 0 ? [`${days}${options?.dayFormat}${options?.allowProperNoun && days > 1 ? "s" : ""}`] : []),
	]
		.join(", ")
		.replace(/,(?!.*,)/gim, ` and`);
};

export const generateDateOfBirthForNoteOutput = (
	dateOfBirth = "",
	dateFormat = "DD/MM/YYYY",
	allowAgeCalculations = true
) => {
	const m = parsePatientDateOfBirthMoment(dateOfBirth, dateFormat);
	if (!m.isValid()) {
		return "-";
	}
	const formattedDate = m.format("MMMM D YYYY");
	if (!allowAgeCalculations) {
		return formattedDate;
	}
	const age = calculateAgeFromBirthDate(dateOfBirth, dateFormat);
	if (!age) {
		return formattedDate;
	}
	return `${formattedDate} (${formatAgeForNoteOutput(age)} old)`;
};

export const preparePrescriptionPrintHeaderData = ({
	user,
	locationState,
	patientName,
	note,
	isCanadianTenant,
	patientAddress,
	isReferral,
	isOrder,
}) => ({
	...(user?.signature ? { signature: user?.signature || "" } : {}),
	...(locationState?.signature ? { signature: locationState.signature } : {}),
	patient_name: patientName?.trim() ? patientName : "",
	date_of_birth: note?.patient_plain_text?.date_of_birth || "",
	health_card_number: (isCanadianTenant && note?.patient_plain_text?.health_card_number) || "",
	hc_version_code: (isCanadianTenant && note?.patient_plain_text?.hc_version_code) || "",
	passport_id: (!isCanadianTenant && note?.patient_plain_text?.passport_id) || "",
	address: patientAddress?.trim() || "",
	phone: note?.patient_plain_text?.mobile || "",
	...(locationState?.physician_name || note?.user?.name
		? { doctor_name: locationState?.physician_name || note?.user?.name || "" }
		: {}),
	...(locationState?.business_phone ? { doctor_phone: locationState.business_phone } : {}),
	...(locationState?.business_name ? { business_name: locationState.business_name } : {}),
	...(isReferral || isOrder ? { remarks: note?.internal_referral?.remarks || "" } : {}),
});
